import { useCallback } from 'react';
import {
  Badge,
  BadgeSizes,
  BadgeVariants,
  Button,
  ButtonSizes,
  ButtonVariants,
  TableCellTypes,
  Tooltip,
} from '@carboledger/ui';
import { CellText, makeSerialCell } from '@/components/common';
import { formatNumber } from '@/lib/format/number';
import { useTranslations } from '@/hooks';
import { SITE_TERMINAL_COLUMNS } from './sites.constants';
import styles from './siteTerminalsColumns.module.scss';

const makeMaterialCountCell = (t) => {
  const renderMaterialCount = (ctx) => {
    const { materialCount } = ctx.row;
    if (materialCount == null) return <CellText>-</CellText>;
    if (materialCount === 0)
      return (
        <Badge variant={BadgeVariants.default} size={BadgeSizes.sm}>
          {t('SITES_TERMINAL_NO_MATERIALS')}
        </Badge>
      );

    return <CellText>{formatNumber(materialCount)}</CellText>;
  };
  return renderMaterialCount;
};

const TerminalRowActions = ({ row, onEdit, onDelete }) => {
  const t = useTranslations();

  const handleEdit = useCallback(() => onEdit(row), [onEdit, row]);
  const handleDelete = useCallback(() => onDelete(row), [onDelete, row]);

  return (
    <span className={styles.actions}>
      <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={handleEdit}>
        {t('COMMON_EDIT')}
      </Button>

      <Tooltip content={t('SITES_TERMINAL_DELETE_LOCKED')} disabled={row.isDeletable}>
        <Button
          variant={ButtonVariants.tertiary}
          size={ButtonSizes.sm}
          disabled={!row.isDeletable}
          onClick={handleDelete}
        >
          {t('COMMON_DELETE')}
        </Button>
      </Tooltip>
    </span>
  );
};

const makeActionsCell = ({ onEdit, onDelete }) => {
  const renderActions = (ctx) => (
    <TerminalRowActions row={ctx.row} onEdit={onEdit} onDelete={onDelete} />
  );
  return renderActions;
};

export const buildSiteTerminalsColumns = (t, { onEdit, onDelete }) => [
  {
    id: SITE_TERMINAL_COLUMNS.serialNo,
    header: t('FIELD_SERIAL_NO'),
    type: TableCellTypes.custom,
    width: 80,
    cell: makeSerialCell(),
  },
  {
    id: SITE_TERMINAL_COLUMNS.terminalName,
    header: t('FIELD_TERMINAL_NAME'),
    type: TableCellTypes.text,
    width: 180,
    pinnable: true,
  },
  {
    id: SITE_TERMINAL_COLUMNS.terminalId,
    header: t('FIELD_TERMINAL_ID'),
    type: TableCellTypes.text,
    width: 160,
  },
  {
    id: SITE_TERMINAL_COLUMNS.materialCount,
    header: t('SITES_TERMINAL_COL_MATERIALS_ASSIGNED'),
    type: TableCellTypes.custom,
    width: 200,
    cell: makeMaterialCountCell(t),
  },
  {
    id: SITE_TERMINAL_COLUMNS.addressLine1,
    header: t('FIELD_ADDRESS_LINE_1'),
    type: TableCellTypes.text,
    width: 220,
  },
  {
    id: SITE_TERMINAL_COLUMNS.addressLine2,
    header: t('FIELD_ADDRESS_LINE_2'),
    type: TableCellTypes.text,
    width: 220,
  },
  {
    id: SITE_TERMINAL_COLUMNS.city,
    header: t('FIELD_CITY_NAME'),
    type: TableCellTypes.text,
    width: 160,
  },
  {
    id: SITE_TERMINAL_COLUMNS.country,
    header: t('FIELD_COUNTRY_NAME'),
    type: TableCellTypes.text,
    width: 160,
  },
  {
    id: SITE_TERMINAL_COLUMNS.zipCode,
    header: t('FIELD_ZIP_CODE'),
    type: TableCellTypes.text,
    width: 120,
  },
  {
    id: SITE_TERMINAL_COLUMNS.actions,
    header: t('SITES_COL_ACTIONS'),
    type: TableCellTypes.custom,
    width: 160,
    isActions: true,
    reorderable: false,
    resizable: false,
    cell: makeActionsCell({ onEdit, onDelete }),
  },
];
